import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faHeart,
  faTasks,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { db } from "@lib/firebase";
import { collection, query, where, getDocs, limit } from "firebase/firestore";

function Volunteer() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [notificationShown, setNotificationShown] = useState(false);

  const checkRegistration = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, "pendaftaranVolunteers"),
        where("uid", "==", user.uid),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setRegistrationStatus("registered");
        // Hanya tampilkan notifikasi jika query parameter registered=true dan belum ditampilkan
        if (searchParams.get("registered") === "true" && !notificationShown) {
          showNotification("info", "Anda sudah terdaftar sebagai volunteer.");
          setNotificationShown(true);
          // Hapus query parameter setelah notifikasi ditampilkan
          setSearchParams({}, { replace: true });
        }
      } else {
        setRegistrationStatus("not_registered");
      }
    } catch (err) {
      showNotification("error", "Gagal memeriksa status pendaftaran.");
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/Login");
      showNotification(
        "error",
        "Silakan login untuk mendaftar sebagai volunteer."
      );
      return;
    }
    checkRegistration();
  }, [
    user,
    navigate,
    showNotification,
    searchParams,
    notificationShown,
    setSearchParams,
  ]);

  const handleVolunteerRegister = () => {
    navigate("/form-volunteer");
  };

  return (
    <section className="w-full min-h-screen text-lime-600 relative bg-white">
      <div
        className="relative w-full h-[60vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url("/bg/volunteer.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-lime-600/100 to-lime-600/20 flex flex-col items-center justify-center gap-6">
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            <FontAwesomeIcon
              icon={faUsers}
              className="text-white text-5xl z-10"
              aria-label="Ikon Volunteer"
            />
          </motion.div>
          <motion.h1
            className="text-3xl md:text-5xl font-black uppercase text-white px-4 text-center"
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.2,
              ease: [0.4, 0, 0.2, 1],
            }}
          >
            Pendaftaran Volunteer JOTR 2025
          </motion.h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <motion.div
          className="space-y-8 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <div className="text-center">
            <p className="text-lg leading-relaxed text-gray-700">
              Bergabunglah sebagai volunteer untuk mendukung kesuksesan JOTR
              2025! Sebagai volunteer, Anda akan menjadi bagian dari tim yang
              membantu kelancaran acara di berbagai bagian, seperti crew water
              station, racepack collection, marshalls, logistik, medis, dan
              berbagai kriteria lain sesuai kebutuhan di lapangan. Pendaftaran
              gratis!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="text-2xl text-lime-600"
              />
              <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                Tanggal Acara
              </span>
              <div className="text-sm text-gray-700">
                27 - 28 September 2025
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
              <FontAwesomeIcon
                icon={faTasks}
                className="text-2xl text-lime-600"
              />
              <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                Tugas Volunteer
              </span>
              <div className="text-sm text-gray-700 text-center">
                Berkontribusi untuk mendukung kelancaran dan kesuksesan acara.
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
              <FontAwesomeIcon
                icon={faHeart}
                className="text-2xl text-lime-600"
              />
              <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                Benefit
              </span>
              <div className="text-sm text-gray-700 text-center">
                Sertifikat, jersey/vest, dan pengalaman berharga.
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-lime-600 mb-4">
              Persyaratan Volunteer
            </h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Berusia minimal 17 tahun.</li>
              <li>
                Memiliki semangat kerja tim dan dedikasi untuk membantu acara.
              </li>
              <li>Sehat jasmani dan mampu bekerja di lapangan.</li>
              <li>
                Diprioritaskan bagi yang memiliki pengalaman sebagai volunteer.
              </li>
            </ul>
          </div>

          <div className="mt-6">
            {registrationStatus === "registered" ? (
              <span className="w-full text-center uppercase px-6 py-4 block bg-lime-600 text-white rounded-xl">
                ANDA SUDAH TERDAFTAR SEBAGAI VOLUNTEER
              </span>
            ) : (
              <div className="space-y-4">
                <button
                  onClick={handleVolunteerRegister}
                  className="w-full text-center uppercase px-6 py-4 block bg-lime-600 text-white rounded-xl hover:bg-lime-700 transition-colors"
                >
                  DAFTAR SEKARANG
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Volunteer;
